import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "../../hooks/useAuth";
import { useSquadre } from "../../hooks/useFase7";
import { api } from "../../lib/api";

import { MobileShell } from "../shell/MobileShell";
import { etichetta } from "../common/etichette";
import { tr } from "../../i18n";
type Ruolo = "presidente_lega" | "admin" | "tesoriere" | "mod_tm" | "mod_mercato" | "partecipante";

interface UserItem {
  id: string;
  email: string;
  nome_completo: string;
  ruolo_lega: Ruolo;
  ruoli_aggiuntivi: string[] | null;
  is_fondatore: boolean;
  attivo: boolean;
  deve_cambiare_password: boolean;
  created_at: string;
  updated_at: string;
  /** Nome squadra che l'utente aveva scelto al momento della registrazione
   * (dall'invito). Andrea 2026-05-19: pre-popolare il rename quando admin
   * gli assegna manualmente una squadra TBD. */
  nome_squadra_richiesto?: string | null;
  /** Squadra di cui e' allenatore in seconda (Andrea 2026-10-08). */
  secondo_di?: string | null;
}

interface ResetPwdResponse {
  user_id: string;
  password_temporanea: string;
}

const RUOLI: Ruolo[] = [
  "presidente_lega",
  "admin",
  "tesoriere",
  "mod_tm",
  "mod_mercato",
  "partecipante",
];

const RUOLO_COLOR: Record<Ruolo, string> = {
  presidente_lega: "bg-accent/20 text-accent",
  admin: "bg-info/20 text-info",
  tesoriere: "bg-success/20 text-success",
  mod_tm: "bg-warning/20 text-warning",
  mod_mercato: "bg-warning/20 text-warning",
  partecipante: "bg-fg-muted/20 text-fg-muted",
};

export function UsersAdminPage() {
  const { user: me } = useAuth();
  const qc = useQueryClient();

  const isPresLega =
    me?.ruolo_lega === "presidente_lega" || me?.ruoli_aggiuntivi?.includes("presidente_lega");

  const { data: users, isLoading } = useQuery({
    queryKey: ["admin", "users"],
    queryFn: async () => (await api.get<UserItem[]>("/admin/users")).data,
  });

  const cambiaRuoloMut = useMutation({
    mutationFn: async (args: {
      userId: string;
      ruolo_lega: Ruolo;
      ruoli_aggiuntivi?: string[];
      is_fondatore?: boolean;
    }) => {
      const res = await api.patch<UserItem>(`/admin/users/${args.userId}/ruolo`, {
        ruolo_lega: args.ruolo_lega,
        ruoli_aggiuntivi: args.ruoli_aggiuntivi,
        is_fondatore: args.is_fondatore,
      });
      return res.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin", "users"] }),
  });

  const cambiaAttivoMut = useMutation({
    mutationFn: async (args: { userId: string; attivo: boolean }) => {
      const res = await api.patch<UserItem>(`/admin/users/${args.userId}/attivo`, {
        attivo: args.attivo,
      });
      return res.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin", "users"] }),
  });

  // Andrea 2026-08-31: eliminazione DEFINITIVA (solo Presidente di Lega).
  // Il backend rifiuta con 409 se l'utente ha dati storici collegati.
  const eliminaMut = useMutation({
    mutationFn: async (userId: string) => {
      await api.delete(`/admin/users/${userId}`);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "users"] });
      qc.invalidateQueries({ queryKey: ["squadre"] });
    },
  });

  const resetPwdMut = useMutation({
    mutationFn: async (args: { userId: string; motivazione: string }) => {
      const res = await api.post<ResetPwdResponse>(`/admin/users/${args.userId}/reset-password`, {
        motivazione: args.motivazione,
      });
      return res.data;
    },
    onSuccess: (data) => {
      // Mostriamo la password temporanea UNA volta (out-of-band)
      alert(
        tr("Password temporanea per l'utente:\\n\\n{{v1}}\\n\\n", {
          v1: data.password_temporanea,
        }) +
          tr(
            "Comunicala out-of-band (Discord/SMS). L'utente sarà costretto a cambiarla al primo login.",
          ),
      );
    },
  });

  // Squadre + mappa user → squadra. Andrea 2026-05-19: utenti senza
  // squadra possono essere assegnati a una TBD libera (fix per inviti
  // creati senza squadra_placeholder).
  const { data: squadre } = useSquadre();
  const squadraByPresidente = useMemo(() => {
    const m = new Map<string, { id: string; nome: string }>();
    for (const s of squadre ?? []) {
      if (s.presidente_user_id) m.set(s.presidente_user_id, { id: s.id, nome: s.nome });
    }
    return m;
  }, [squadre]);
  const squadreLibere = (squadre ?? []).filter((s) => !s.presidente_user_id);

  const assegnaSquadraMut = useMutation({
    mutationFn: async (args: { squadraId: string; userId: string; nuovoNome?: string | null }) => {
      const res = await api.post<{
        squadra_id: string;
        presidente_user_id: string;
        nome: string;
      }>(`/admin/squadre/${args.squadraId}/assegna-presidente`, {
        user_id: args.userId,
        nuovo_nome_squadra: args.nuovoNome ?? null,
      });
      return res.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["squadre"] });
      qc.invalidateQueries({ queryKey: ["admin", "users"] });
    },
  });

  // Andrea 2026-05-23: rinomina squadra dal pannello admin. L'endpoint
  // PATCH /squadre/:id/nome accetta gia' admin oltre al proprietario;
  // 1° cambio gratis, successivi -25M dal budget stagione attiva.
  const rinominaSquadraMut = useMutation({
    mutationFn: async (args: { squadraId: string; nuovoNome: string }) => {
      const res = await api.patch<{
        nome: string;
        numero_cambi_nome: number;
        penale_applicata_milioni: string | null;
      }>(`/squadre/${args.squadraId}/nome`, { nuovo_nome: args.nuovoNome });
      return res.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["squadre"] });
      qc.invalidateQueries({ queryKey: ["admin", "users"] });
    },
  });

  const [filterText, setFilterText] = useState("");
  const filtered = users?.filter((u) => {
    if (!filterText) return true;
    const f = filterText.toLowerCase();
    return (
      u.email.toLowerCase().includes(f) ||
      u.nome_completo.toLowerCase().includes(f) ||
      u.ruolo_lega.toLowerCase().includes(f)
    );
  });

  return (
    <MobileShell logoSuffix="UTENTI">
      <h2 className="font-display text-3xl text-accent tracking-wide">{tr("Gestione utenti")}</h2>

      <div className="card">
        <input
          type="text"
          placeholder={tr("filtra per email / nome / ruolo…")}
          value={filterText}
          onChange={(e) => setFilterText(e.target.value)}
          className="w-full px-3 py-2 bg-primary-deep border border-primary-soft rounded-lg text-fg"
        />
      </div>

      {isLoading && <div className="card text-fg-muted">{tr("Caricamento…")}</div>}

      {filtered && (
        <div className="card p-0 overflow-hidden">
          {filtered.length === 0 && (
            <div className="p-6 text-center text-fg-muted">{tr("Nessun utente trovato.")}</div>
          )}
          {filtered.map((u) => {
            const squadraAssegnata = squadraByPresidente.get(u.id);
            return (
              <div
                key={u.id}
                className={`p-4 border-b border-primary/10 last:border-b-0 ${
                  !u.attivo ? "opacity-50" : ""
                }`}
              >
                <div className="flex flex-wrap items-center gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="font-bold">{u.nome_completo}</div>
                    <div className="text-xs text-fg-muted font-mono">{u.email}</div>
                  </div>
                  <span className={`pill ${RUOLO_COLOR[u.ruolo_lega]}`}>
                    {etichetta(u.ruolo_lega)}
                  </span>
                  {u.ruoli_aggiuntivi?.map((r) => (
                    <span
                      key={r}
                      className={`pill text-xs ${RUOLO_COLOR[r as Ruolo] || "bg-fg-muted/20"}`}
                    >
                      +{r}
                    </span>
                  ))}
                  {u.is_fondatore && (
                    <span className="pill bg-accent text-ink font-bold">{tr("FONDATORE")}</span>
                  )}
                  {!u.attivo && (
                    <span className="pill bg-danger/20 text-danger">{tr("DISATTIVATO")}</span>
                  )}
                </div>

                {/* Azioni */}
                <div className="flex flex-wrap gap-2 mt-3 items-center">
                  {isPresLega && (
                    <>
                      {/* Ruolo primario: bloccato su se stessi per evitare lock-out */}
                      <label className="text-xs text-fg-muted">{tr("Primario:")}</label>
                      <select
                        value={etichetta(u.ruolo_lega)}
                        disabled={u.id === me?.id}
                        title={
                          u.id === me?.id
                            ? tr("Non puoi cambiare il TUO ruolo primario (rischio lock-out)")
                            : ""
                        }
                        onChange={(e) =>
                          cambiaRuoloMut.mutate({
                            userId: u.id,
                            ruolo_lega: e.target.value as Ruolo,
                            ruoli_aggiuntivi: u.ruoli_aggiuntivi ?? [],
                          })
                        }
                        className="px-2 py-1 bg-primary-deep border border-primary-soft rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {RUOLI.map((r) => (
                          <option key={r} value={r}>
                            {r}
                          </option>
                        ))}
                      </select>
                      <label className="text-xs text-fg-muted ml-2">{tr("Aggiuntivi:")}</label>
                      {/* Ruoli aggiuntivi: modificabili anche su se stessi (no lock-out) */}
                      {RUOLI.filter((r) => r !== u.ruolo_lega && r !== "partecipante").map((r) => {
                        const checked = (u.ruoli_aggiuntivi ?? []).includes(r);
                        return (
                          <label
                            key={r}
                            className="flex items-center gap-1 text-xs cursor-pointer bg-primary-deep/40 border border-primary/20 rounded px-2 py-1"
                          >
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={(e) => {
                                const current = u.ruoli_aggiuntivi ?? [];
                                const next = e.target.checked
                                  ? [...current, r]
                                  : current.filter((x) => x !== r);
                                cambiaRuoloMut.mutate({
                                  userId: u.id,
                                  ruolo_lega: u.ruolo_lega,
                                  ruoli_aggiuntivi: next,
                                });
                              }}
                            />
                            {r}
                          </label>
                        );
                      })}
                    </>
                  )}
                  {isPresLega && (
                    <button
                      onClick={() =>
                        cambiaRuoloMut.mutate({
                          userId: u.id,
                          ruolo_lega: u.ruolo_lega,
                          ruoli_aggiuntivi: u.ruoli_aggiuntivi ?? [],
                          is_fondatore: !u.is_fondatore,
                        })
                      }
                      className="btn-ghost text-xs"
                    >
                      {u.is_fondatore ? tr("Rimuovi fondatore") : tr("Marca fondatore")}
                    </button>
                  )}
                  <button
                    onClick={() => {
                      const m = prompt(
                        tr("Motivazione del reset password (sarà loggata in audit):"),
                      );
                      if (m) resetPwdMut.mutate({ userId: u.id, motivazione: m });
                    }}
                    disabled={resetPwdMut.isPending}
                    className="btn-ghost text-xs border border-warning/40 text-warning"
                  >
                    {tr("🔑 Reset password")}
                  </button>
                  {u.id !== me?.id && (
                    <button
                      onClick={() =>
                        cambiaAttivoMut.mutate({
                          userId: u.id,
                          attivo: !u.attivo,
                        })
                      }
                      className={`btn-ghost text-xs border ${
                        u.attivo ? "border-danger/40 text-danger" : "border-success/40 text-success"
                      }`}
                    >
                      {u.attivo ? tr("Disattiva") : tr("Riattiva")}
                    </button>
                  )}
                  {isPresLega && u.id !== me?.id && !u.is_fondatore && (
                    <button
                      onClick={async () => {
                        if (
                          !confirm(
                            tr("ELIMINARE DEFINITIVAMENTE l'account di {{v1}} ({{v2}})?\\n\\n", {
                              v1: u.nome_completo,
                              v2: u.email,
                            }) +
                              tr(
                                "L'account e i suoi accessi spariscono per sempre; una eventuale ",
                              ) +
                              tr("squadra presieduta torna libera. Non si può annullare.\n\n") +
                              tr("Se l'utente ha storico di lega (budget, mercato, voti) ") +
                              tr("l'eliminazione verrà rifiutata: in quel caso usa Disattiva."),
                          )
                        )
                          return;
                        if (
                          !confirm(
                            tr("Ultima conferma: eliminare {{v1}}?", { v1: u.nome_completo }),
                          )
                        )
                          return;
                        try {
                          await eliminaMut.mutateAsync(u.id);
                          alert(tr("✓ Account di {{v1}} eliminato.", { v1: u.nome_completo }));
                        } catch (err) {
                          const e2 = err as {
                            response?: { data?: { detail?: string } };
                            message?: string;
                          };
                          alert(
                            tr("Errore: ") + (e2.response?.data?.detail ?? e2.message ?? "errore"),
                          );
                        }
                      }}
                      disabled={eliminaMut.isPending}
                      className="btn-ghost text-xs border border-danger text-danger font-bold disabled:opacity-50"
                    >
                      {tr("🗑 Elimina")}
                    </button>
                  )}
                </div>

                {/* Sezione squadra: mostra quella assegnata, oppure dropdown
                    di assegnazione se l'utente è orfano (caso bug Andrea
                    2026-05-19). Solo per utenti attivi. */}
                {u.attivo && (
                  <div className="mt-3 pt-3 border-t border-primary/10 text-xs flex items-center gap-2 flex-wrap">
                    <span className="text-fg-muted uppercase tracking-wider">{tr("Squadra:")}</span>
                    {squadraAssegnata ? (
                      <>
                        <span className="font-bold text-accent">⚜ {squadraAssegnata.nome}</span>
                        <button
                          type="button"
                          onClick={async () => {
                            const nuovo = prompt(
                              tr("Nuovo nome per la squadra di {{v1}}\\n", {
                                v1: u.nome_completo,
                              }) +
                                tr('(attuale: "{{v1}}")\\n\\n', { v1: squadraAssegnata.nome }) +
                                tr(
                                  "1° cambio gratis. Successivi: penale -25M sulla stagione attiva.",
                                ),
                              squadraAssegnata.nome,
                            );
                            const trimmed = nuovo?.trim();
                            if (!trimmed || trimmed === squadraAssegnata.nome) return;
                            try {
                              const res = await rinominaSquadraMut.mutateAsync({
                                squadraId: squadraAssegnata.id,
                                nuovoNome: trimmed,
                              });
                              const penale = res.penale_applicata_milioni
                                ? tr("\\nPenale di {{v1}}M addebitata.", {
                                    v1: res.penale_applicata_milioni,
                                  })
                                : tr("\nPrimo cambio gratis.");
                              alert(
                                tr('✓ Squadra rinominata in "{{v1}}".{{v2}}', {
                                  v1: res.nome,
                                  v2: penale,
                                }),
                              );
                            } catch (err) {
                              const e2 = err as {
                                response?: { data?: { detail?: string } };
                                message?: string;
                              };
                              alert(
                                tr("Errore: ") +
                                  (e2.response?.data?.detail ?? e2.message ?? "errore"),
                              );
                            }
                          }}
                          disabled={rinominaSquadraMut.isPending}
                          className="btn-ghost text-[10px] border border-accent/40 text-accent disabled:opacity-50"
                        >
                          {tr("✏️ Rinomina")}
                        </button>
                      </>
                    ) : (
                      <>
                        {/* Andrea 2026-10-08: il secondo allenatore non e' "senza
                            squadra"; "nessuna" resta per chi guarda soltanto */}
                        {u.secondo_di ? (
                          <span className="font-bold text-accent">
                            👥 Secondo allenatore di {u.secondo_di}
                          </span>
                        ) : (
                          <span className="text-danger">{tr("— nessuna —")}</span>
                        )}
                        {squadreLibere.length > 0 ? (
                          <>
                            <select
                              defaultValue=""
                              onChange={async (e) => {
                                const sqId = e.target.value;
                                if (!sqId) return;
                                const sq = squadreLibere.find((x) => x.id === sqId);
                                if (!sq) return;
                                // Default rinomina: il nome che l'utente
                                // aveva indicato in registrazione (se c'è),
                                // altrimenti vuoto. Andrea 2026-05-19.
                                const defaultNome = u.nome_squadra_richiesto ?? "";
                                const hint = defaultNome
                                  ? tr(
                                      '\\n\\nNome scelto in registrazione: "{{v1}}" (pre-compilato).',
                                      { v1: defaultNome },
                                    )
                                  : "";
                                const rename = prompt(
                                  tr('Assegnare "{{v1}}" a {{v2}}?\\n\\n', {
                                    v1: sq.nome,
                                    v2: u.nome_completo,
                                  }) +
                                    tr(
                                      "Opzionalmente rinomina la squadra (1° rinomina gratis).\n",
                                    ) +
                                    tr("Lascia vuoto per tenere il nome attuale:") +
                                    hint,
                                  defaultNome,
                                );
                                if (rename === null) {
                                  e.currentTarget.value = "";
                                  return;
                                }
                                try {
                                  await assegnaSquadraMut.mutateAsync({
                                    squadraId: sqId,
                                    userId: u.id,
                                    nuovoNome: rename.trim() || null,
                                  });
                                  alert(
                                    tr("✓ {{v1}} è ora presidente di {{v2}}", {
                                      v1: u.nome_completo,
                                      v2: rename.trim() || sq.nome,
                                    }),
                                  );
                                } catch (err) {
                                  const e2 = err as {
                                    response?: { data?: { detail?: string } };
                                    message?: string;
                                  };
                                  alert(
                                    tr("Errore: ") +
                                      (e2.response?.data?.detail ?? e2.message ?? "errore"),
                                  );
                                }
                              }}
                              disabled={assegnaSquadraMut.isPending}
                              className="px-2 py-1 bg-primary-deep border border-primary-soft rounded text-xs"
                            >
                              <option value="">{tr("— assegna a squadra TBD —")}</option>
                              {squadreLibere.map((s) => (
                                <option key={s.id} value={s.id}>
                                  {s.nome}
                                </option>
                              ))}
                            </select>
                          </>
                        ) : u.secondo_di ? null : (
                          <span className="text-fg-muted italic">
                            {tr("spettatore · nessuna squadra libera disponibile")}
                          </span>
                        )}
                      </>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </MobileShell>
  );
}
